import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { EditIcon, TrashIcon, UsersIcon } from '@/components/icons';
import { userRoleMap } from '@/config/statusMaps';
import { formatDate } from '@/utils/date';
import type { User } from '@/types/user';

export interface UsersTableProps {
  users: User[];
  isLoading: boolean;
  currentUserId?: string;
  onAddFirst: () => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

const COLUMN_COUNT = 5;

/**
 * Role badge
 */
function RoleBadge({ role }: { role: string }) {
  const config = userRoleMap.get(role);
  return <span className={`users-role ${config.className}`}>{config.label}</span>;
}

/**
 * Users table. The signed-in user cannot delete their own account.
 */
export function UsersTable({ users, isLoading, currentUserId, onAddFirst, onEdit, onDelete }: UsersTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Email</TableHeader>
          <TableHeader>Role</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Created</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {users.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<UsersIcon />}
                message="No users found"
                action={
                  <Button variant="secondary" size="sm" onClick={onAddFirst}>
                    Add your first user
                  </Button>
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          users.map((user) => {
            const isSelf = user.id === currentUserId;

            return (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="users-email-cell">
                    <span className="users-email">{user.email}</span>
                    {isSelf && <span className="users-current-badge">You</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <RoleBadge role={user.role} />
                </TableCell>
                <TableCell>
                  <span className={`users-status ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                    <span className="users-status-dot" />
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="users-date">{formatDate(user.created_at)}</span>
                </TableCell>
                <TableCell align="right">
                  <div className="users-actions">
                    <IconButton
                      className="users-action-btn"
                      icon={<EditIcon />}
                      label={`Edit ${user.email}`}
                      title="Edit user"
                      onClick={() => onEdit(user)}
                    />
                    {!isSelf && (
                      <IconButton
                        className="users-action-btn users-action-btn-danger"
                        icon={<TrashIcon />}
                        label={`Delete ${user.email}`}
                        title="Delete user"
                        onClick={() => onDelete(user)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
